// JournalInput.tsx - 用戶日記輸入組件
import React, { useState, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronUp, MessageSquare, Shield } from 'lucide-react';
import { generateReflectionQuestionsSimple } from '../../../services/api/reflectionQuestions';
import { logger } from '../../../utils/logger';

/**
 * 錯誤類型定義
 */
type JournalError = {
  type: 'api_error' | 'fallback_used' | 'network_error';
  message: string;
};

/**
 * 日記輸入組件的常數
 */
const JOURNAL_CONSTANTS = {
  DEFAULT_QUESTIONS: [
    "What went well today?",
    "What could have gone better?",
    "What are you grateful for today?",
  ],
  ERROR_MESSAGES: {
    API_ERROR: 'Unable to load personalized questions',
    FALLBACK_USED: 'Using default questions',
    NETWORK_ERROR: 'Network connection issue'
  },
  ANIMATION_DURATION: 200,
  TEXTAREA_ROWS: {
    EXPANDED: 6,
    COLLAPSED: 8
  },
  PLACEHOLDERS: {
    TEXTAREA: "Share what's on your mind about today... Your thoughts, feelings, insights, or anything that feels important to capture."
  }
} as const;

/**
 * JournalInput 組件的 Props 定義
 */
interface JournalInputProps {
  /** 用戶 ID */
  userId: string;
  /** 目前的日記內容 */
  content: string;
  /** 內容變更回調函數 */
  onContentChange: (content: string) => void;
  /** 是否正在載入 */
  isLoading?: boolean;
}

const JournalInput: React.FC<JournalInputProps> = React.memo(({
  userId,
  content,
  onContentChange,
  isLoading = false
}) => {
  const [promptQuestions, setPromptQuestions] = useState<string[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsError, setQuestionsError] = useState<JournalError | null>(null);

  /**
   * 處理 API 錯誤並設定適當的錯誤狀態
   */
  const handleError = useCallback((error: unknown, fallbackType: JournalError['type'] = 'api_error') => {
    logger.error('JournalInput', 'Error loading journal questions', error);

    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    let errorType: JournalError['type'] = fallbackType;
    let displayMessage: string = JOURNAL_CONSTANTS.ERROR_MESSAGES.API_ERROR;

    // 根據錯誤類型設定適當的訊息
    if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
      errorType = 'network_error';
      displayMessage = JOURNAL_CONSTANTS.ERROR_MESSAGES.NETWORK_ERROR;
    }

    setQuestionsError({
      type: errorType,
      message: displayMessage
    });

    // 設定回退問題
    setPromptQuestions([...JOURNAL_CONSTANTS.DEFAULT_QUESTIONS]);
    setPromptsLoaded(true);
  }, []);

  const [showPrompts, setShowPrompts] = useState(false);
  const [promptsLoaded, setPromptsLoaded] = useState(false);

  // Default fallback questions (從常數中取得)
  const fallbackQuestions = useMemo(() => [...JOURNAL_CONSTANTS.DEFAULT_QUESTIONS], []);

  /**
   * 按需載入 AI 生成的日記提示問題
   * 只有在用戶首次展開提示時才會載入
   */
  const loadJournalQuestions = useCallback(async () => {
    if (promptsLoaded) return; // Don't reload if already loaded

    try {
      setQuestionsLoading(true);
      setQuestionsError(null);

      const today = new Date();
      const dateString = today.getFullYear() + '-' +
        String(today.getMonth() + 1).padStart(2, '0') + '-' +
        String(today.getDate()).padStart(2, '0');

      const result = await generateReflectionQuestionsSimple(dateString);

      setPromptQuestions(result.questions.length > 0 ? result.questions : fallbackQuestions);
      setPromptsLoaded(true);

    } catch (error) {
      handleError(error, 'fallback_used');
    } finally {
      setQuestionsLoading(false);
    }
  }, [promptsLoaded, handleError, fallbackQuestions]);

  /**
   * 處理切換提示可見性
   * 在首次展開時觸發問題載入
   */
  const handleTogglePrompts = useCallback(async () => {
    const newShowPrompts = !showPrompts;
    setShowPrompts(newShowPrompts);

    // Load questions when expanding for the first time
    if (newShowPrompts && !promptsLoaded) {
      await loadJournalQuestions();
    }
  }, [showPrompts, promptsLoaded, loadJournalQuestions]);


  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Simplified intro */}
      <div className="mb-5">
        <p className="text-sm text-gray-600">
          Capture your thoughts and insights from today to clear your mind.
        </p>
      </div>

      {/* Writing Prompts Section */}
      <div className="mb-6">
        <button
          onClick={handleTogglePrompts}
          className="flex items-center p-3 w-full text-left bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors duration-200"
          type="button"
          aria-expanded={showPrompts}
          aria-controls="writing-prompts-content"
          aria-label={showPrompts ? '隱藏寫作提示' : '顯示寫作提示'}
        >
          <MessageSquare className="w-5 h-5 text-gray-500 mr-3 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-700 flex-grow">
            {showPrompts ? 'Hide writing prompts' : 'Need writing prompts?'}
          </span>
          {showPrompts ? (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </button>

        {/* Collapsible content with animation */}
        {showPrompts && (
          <div
            id="writing-prompts-content"
            className="mt-3 transition-all duration-200 ease-out transform translate-y-0 opacity-100"
            aria-live="polite"
          >
            {questionsLoading ? (
              <div className="space-y-3 p-4 bg-white border border-gray-200 rounded-lg">
                <div className="flex items-center text-sm text-gray-500 mb-3">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading personalized prompts...
                </div>
                {[1, 2, 3].map((index) => (
                  <div
                    key={index}
                    className="p-3 bg-gray-100 rounded-md animate-pulse"
                  >
                    <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-1">
                {questionsError && (
                  <div className="text-xs text-amber-600 mb-3 p-2 bg-amber-50 rounded border border-amber-200">
                    {questionsError.message}
                  </div>
                )}
                <div className="space-y-2 pl-2">
                  {promptQuestions.map((question, index) => (
                    <div
                      key={index}
                      className="flex items-start text-sm text-gray-600 leading-relaxed"
                    >
                      <span className="mr-2 text-gray-400">•</span>
                      <span>{question}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Journal Writing Section */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-900 mb-3">
          Write your journal
        </label>
        <textarea
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent resize-none text-sm leading-relaxed placeholder-gray-400"
          rows={showPrompts ? JOURNAL_CONSTANTS.TEXTAREA_ROWS.EXPANDED : JOURNAL_CONSTANTS.TEXTAREA_ROWS.COLLAPSED}
          value={content}
          onChange={(e) => onContentChange(e.target.value)}
          placeholder={JOURNAL_CONSTANTS.PLACEHOLDERS?.TEXTAREA || "Share what's on your mind about today... Your thoughts, feelings, insights, or anything that feels important to capture."}
          disabled={isLoading}
        />
      </div>

      {/* Bottom row: Character count + Privacy info */}
      <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
        <div className="flex items-center">
          <Shield className="w-3 h-3 mr-1" />
          <span>Saved privately</span>
        </div>

        {content.length > 0 && (
          <span>{content.length} characters</span>
        )}
      </div>
    </div>
  );
});

// 設定 displayName 以便於除錯
JournalInput.displayName = 'JournalInput';

export default JournalInput;
